import is from '@sindresorhus/is';
import type { EventSourceClient } from 'eventsource-client';
import { createEventSource } from 'eventsource-client';
import Maybe from 'true-myth/maybe';

import type { Pathname, RequestHeaders } from '../http-fetcher/http-fetcher';
import type { HTTPMethod } from '../http-fetcher/http-method';

type Listener = (event: MessageEvent) => void;

export type RealtimeEventListener = {
    readonly addEventListener: (type: string, listener: Listener) => void;
    readonly start: () => void;
    readonly stop: () => void;
};

export type RealtimeEventListenerBuilder = {
    readonly build: ({
        autoReconnect,
        method,
        path,
        payload,
        requestHeaders,
    }: {
        readonly autoReconnect?: boolean;
        readonly method?: 'GET' | 'POST';
        readonly path: Pathname;
        readonly payload?: FormData | string;
        readonly requestHeaders?: RequestHeaders;
    }) => RealtimeEventListener;
    set requestHeaders(_requestHeaders: RequestHeaders);
};

const MAX_RETRIES = 10;

const buildListener: (
    baseUrl: URL,
    path: Pathname,
    requestHeaders: Maybe<RequestHeaders>,
    payload?: FormData | string,
    method?: HTTPMethod,
    autoReconnect?: boolean,
) => RealtimeEventListener = (baseUrl, path, requestHeaders, payload, method = 'POST', autoReconnect = true) => {
    const basePath = baseUrl.pathname.replace(/\/+$/, '');
    const extendedPath = path.replace(/^\/+/, '');
    const actualPath = new URL(`${basePath}/${extendedPath}`, baseUrl);

    const listeners = new Map<string, Set<Listener>>();
    let client: EventSourceClient | null = null;
    let retryCount = 0;

    const dispatch = (type: string, data: string): void => {
        const handlers = listeners.get(type);
        if (is.undefined(handlers)) {
            return;
        }
        const event = new MessageEvent(type, { data });
        for (const handler of handlers) {
            handler(event);
        }
    };

    return {
        addEventListener: (type, listener) => {
            let set = listeners.get(type);
            if (is.undefined(set)) {
                set = new Set();
                listeners.set(type, set);
            }
            set.add(listener);
        },
        start: () => {
            if (!is.null(client)) {
                return;
            }
            retryCount = 0;
            client = createEventSource({
                body: payload,
                credentials: 'include',
                headers: requestHeaders.unwrapOr({}),
                method,
                onConnect: () => {
                    retryCount = 0;
                    dispatch('open', '');
                },
                onDisconnect: () => {
                    if (!autoReconnect) {
                        client?.close();
                        client = null;
                        dispatch('close', '');
                    }
                },
                onMessage: ({ data, event }) => {
                    dispatch(event ?? 'message', data);
                },
                onScheduleReconnect: () => {
                    retryCount += 1;
                    if (retryCount > MAX_RETRIES) {
                        client?.close();
                        client = null;
                        dispatch('close', '');
                    }
                },
                url: actualPath.toString(),
            });
        },
        stop: () => {
            client?.close();
            client = null;
        },
    };
};

export const createRealtimeEventListenerBuilder: (baseUrl: URL) => RealtimeEventListenerBuilder = (baseUrl) => {
    let defaultRequestHeaders: Maybe<RequestHeaders> = Maybe.nothing();

    return {
        build: ({ autoReconnect = true, method = 'POST', path, payload, requestHeaders = {} }) =>
            buildListener(
                baseUrl,
                path,
                defaultRequestHeaders.map((defaultHeaders) => ({ ...defaultHeaders, ...requestHeaders })),
                payload,
                method,
                autoReconnect,
            ),
        set requestHeaders(_requestHeaders: RequestHeaders) {
            defaultRequestHeaders = defaultRequestHeaders.mapOrElse(
                () => Maybe.of(_requestHeaders),
                (headers) =>
                    Maybe.of<RequestHeaders>({
                        ...headers,
                        ..._requestHeaders,
                    }),
            );
        },
    };
};
